import {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  checkRowLock,
  createRowLock,
  pingRowLock,
  removeRowLock,
  setRowLockCurrentUser,
  setRowLockEntity,
  setRowLockCallback,
  takeoverRowLock,
} from '@redux/modules/rowLock';

import {
  selectLockRequiresCheck,
  selectLocked,
  selectLockedBy,
  selectLockedByMe,
  selectImpersonatedBy,
  selectLastUpdatedOn,
  selectLastCheckedOn,
  selectCreatedWithinSession,
  selectError,
  selectClareityUrl,
} from '@redux/selectors/RowLockSelector';

import {
  getUserName,
  getUserId,
  getUserIdleStatus,
} from '@redux/selectors/UserSelector';
import { getImpersonationStatus } from '@redux/selectors/ImpersonationSelector';

import { isValidEntity } from '@utils/rowLock';
import useInterval from '@hooks/useInterval';
import useRowChecked from '@hooks/useRowChecked';
import AutoSaveMessage from '@components/rowLock/AutoSaveMessage';
import LockMessage from '@components/rowLock/RowLockMessage';
import LockOwnerMessage from '@components/rowLock/RowLockOwnerMessage';
import { getIsAdmin } from '@redux/selectors/AclSelector';

/* Validates hook props using PropTypes.checkPropTypes. */
const validateProps = props => {
  const propTypes = {
    rowLockEntity: PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.number,
    }),
    isEditOperation: PropTypes.bool,
    enableAutomation: PropTypes.bool,
    redirectOnLocked: PropTypes.bool,
    enableRowLocking: PropTypes.bool,
  };

  PropTypes.checkPropTypes(propTypes, props, 'prop', 'useRowLock');
};

const useRowLock = props => {
  const {
    rowLockEntity = null, // the entity which the hook will be initialized with;
    isEditOperation = false, // specifies if the hook is used within an edit page;
    enableAutomation = true, // specifies if automations (e.g. intervals) should be ON for this hook;
    enableRowLocking = true, // specifies if the row-locking should currently be enabled;
    redirectOnLocked = false, // specifies if the user should be redirected to a dedicated page if the entity is locked
  } = props;

  validateProps(props);

  const [entity, setEntity] = useState(enableRowLocking && isValidEntity(rowLockEntity) ? rowLockEntity : null);
  const [lockCallback, setLockCallback] = useState(null);
  const checked = useRowChecked(entity);

  const history = useHistory(); // use for redirection

  const dispatch = useDispatch();
  const locked = useSelector(selectLocked);
  const lockedBy = useSelector(selectLockedBy);
  const lockedByMe = useSelector(selectLockedByMe);
  const impersonatedBy = useSelector(selectImpersonatedBy);
  const isAdmin = useSelector(getIsAdmin);
  const lockCreatedWithinSession = useSelector(selectCreatedWithinSession);
  const lockRequiresCheck = useSelector(selectLockRequiresCheck);
  const lastUpdatedOn = useSelector(selectLastUpdatedOn);
  const lastCheckedOn = useSelector(selectLastCheckedOn);
  const isError = useSelector(selectError);
  const clareityUrl = useSelector(selectClareityUrl);

  const isErrorOrUnknownStatus = isError || lockRequiresCheck || !!clareityUrl;
  const lockedByMeWithinSession = lockedByMe && lockCreatedWithinSession;
  const lockingAuthorized = !locked || lockedByMeWithinSession;

  const realUserName = useSelector(getUserName);
  const realUserId = useSelector(getUserId);
  const isIdle = useSelector(getUserIdleStatus);
  const impersonationStatus = useSelector(getImpersonationStatus);

  const currentUser = useMemo(() => (impersonationStatus?.ImpersonationActive
    ? { userName: impersonationStatus.memberFullName, userId: impersonationStatus.memberUniqueID }
    : { userName: realUserName, userId: realUserId }
  ), [realUserName, realUserId, impersonationStatus]);

  const authChecked = useSelector(({ authentication }) => authentication.authChecked);
  const isLoadingAuth = useSelector(({ authentication }) => authentication.isLoadingAuth);
  const isAuthenticated = useSelector(({ authentication }) => authentication.isAuthenticated);
  const isAuthCloseToExpiry = useSelector(({ authentication }) => authentication.isAuthCloseToExpiry);

  const rowLockIntervalsEnabled = enableRowLocking && enableAutomation && !isIdle && authChecked && isAuthenticated && !isLoadingAuth && !isAuthCloseToExpiry;

  /* Save current user to redux store. */
  useEffect(() => {
    if (!enableRowLocking) return;
    dispatch(setRowLockCurrentUser(currentUser));
  }, [currentUser]);

  const initializeEntity = (newEntity, callback) => {
    if (!enableRowLocking) return;
    setLockCallback(null);
    if (!isValidEntity(newEntity)) return;
    setEntity(newEntity);
    dispatch(setRowLockEntity(newEntity)).then(() => callback && setLockCallback(() => callback));
  };

  /* Make sure to re-initialize the local entity when a new rowLock entity is being passed. */
  useEffect(() => {
    if (!enableRowLocking) return;
    initializeEntity(rowLockEntity);
  }, [rowLockEntity]);

  const checkStatus = useCallback(callback => {
    if (!enableRowLocking) return;
    if (!isValidEntity(entity)) return;
    dispatch(checkRowLock(entity)).then(() => callback && setLockCallback(() => callback));
  }, [entity]);

  const createLock = useCallback(async callback => {
    let created;

    if (!enableRowLocking) return null;
    if (!isValidEntity(entity)) return null;

    try {
      created = await dispatch(createRowLock(entity));
      if (created && callback) setLockCallback(() => callback);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error: RowLock createLock', err);
    }

    return created;
  }, [entity]);

  const pingLock = useCallback(() => {
    if (!enableRowLocking) return;
    if (!isValidEntity(entity)) return;
    dispatch(pingRowLock(entity));
  }, [entity]);

  const removeLock = useCallback(() => {
    if (!enableRowLocking) return;
    if (!isValidEntity(entity)) return;
    dispatch(removeRowLock(entity));
  }, [entity]);

  const takeoverLock = useCallback(callback => {
    if (!enableRowLocking) return;
    if (!isValidEntity(entity)) return;
    dispatch(takeoverRowLock(entity)).then(() => callback && setLockCallback(() => callback));
  }, [entity]);

  /* It is not very clear at this point what "Discard Changes" action should do behind the scenes, so for now
  it will just take over existing lock and remove it (remove is only allowed for the owner of the lock).
  This approach should also assist in removing any hanging locks. */
  const discardChanges = useCallback(() => {
    if (!enableRowLocking) return;
    if (!isValidEntity(entity)) return;
    dispatch(takeoverRowLock(entity)).then(() => dispatch(removeRowLock(entity)));
  }, [entity]);

  /* Redirect the user to the Clareity URL when this is being returned by the api, and the user is not on an edit page.
  This is meant to be a temporary solution until a middleware will automatically deal with this url. */
  useEffect(() => {
    if (!enableRowLocking) return;
    if (clareityUrl && !isEditOperation && checked) {
      window.location.href = clareityUrl;
    }
  }, [clareityUrl, isEditOperation, checked]);

  /* Re-trigger the lock check action if that is required (due to receiving a 'FAILURE' type). */
  useEffect(() => {
    if (!enableRowLocking) return;
    if (lockRequiresCheck && checked && !isError) {
      checkStatus();
    }
  }, [lockRequiresCheck, checked, isError]);

  /* Check if there is any callback action waiting to be fired. */
  useEffect(() => {
    if (!enableRowLocking) return;
    if (lockCallback && checked && !isErrorOrUnknownStatus && lockedByMeWithinSession) {
      lockCallback();
      setLockCallback(null);
    }
  }, [lockCallback && checked, isErrorOrUnknownStatus, lockedByMeWithinSession]);

  /* Remove any saved callback actions if the user is not entitled to fire it yet. */
  useEffect(() => {
    if (!enableRowLocking) return;
    if (lockCallback && checked && !isErrorOrUnknownStatus && locked && !lockedByMeWithinSession) {
      setLockCallback(null);
    }
  }, [lockCallback, checked, isErrorOrUnknownStatus, locked, lockedByMeWithinSession]);

  /* Redirect the user to the RowLock page if the entity is locked. */
  useEffect(() => {
    if (!enableRowLocking) return;
    if (redirectOnLocked && checked && !isErrorOrUnknownStatus && locked && !lockedByMeWithinSession) {
      if (lockCallback) {
        // Save callback to redux store, from where it will be picked up by the rowLock page.
        dispatch(setRowLockCallback(lockCallback));
      }
      history.push(`/rowLock/${entity.type}/${entity.id}`);
    }
  }, [redirectOnLocked, checked, isErrorOrUnknownStatus, locked, lockedByMeWithinSession, lockCallback, entity]);

  /* If the lock belongs to the current user, then start to regularly ping the lock for heartbeat purposes. */
  useInterval(pingLock, rowLockIntervalsEnabled && isEditOperation && checked && !isErrorOrUnknownStatus && lockedByMeWithinSession ? (1000 * 60 * 5) : null);

  /* If the entity is locked, then start to regularly check for any lock status changes. */
  useInterval(checkStatus, rowLockIntervalsEnabled && checked && !isErrorOrUnknownStatus && locked ? (1000 * 60) : null);

  return {
    rowLockingEnabled: enableRowLocking && isValidEntity(entity),
    entity,
    checked,
    lockingAuthorized: checked ? lockingAuthorized : false,
    locked: checked ? locked : false,
    lockedBy: checked ? lockedBy : null,
    lockedByMe: checked ? lockedByMe : false,
    lockedByMeWithinSession: checked ? lockedByMeWithinSession : false,
    impersonatedBy,
    isAdmin: checked ? isAdmin : false,
    lastUpdatedOn: checked ? lastUpdatedOn : null,
    lastCheckedOn: checked ? lastCheckedOn : null,
    initializeEntity,
    checkStatus,
    createLock,
    pingLock,
    removeLock,
    takeoverLock,
    discardChanges,
    AutoSaveMessage,
    LockMessage,
    LockOwnerMessage,
  };
};

export default useRowLock;
